/*
 * Custom campaigns (Marketing, Campaigns, owner 2026-11-01, after a look at Mailchimp): a location makes its own campaign.
 * Who gets it is a list of rules (tags, work done, plan, visits, money paid, area, how they found us), matched all or any.
 * What they get is a timeline read top to bottom like a text thread: each step is a day, sent by text, email or both, so a
 * text can go on day 0 and an email on day 3. It runs like the other campaigns: weekdays during the day, a few at a time,
 * and it stops for anyone who replies, books new work, or opts out.
 *
 * Kept in the campaigns table under a key "c_" plus 8 letters and numbers; each client's place in it is in
 * campaign_people.state.custom[key]. Shared so the server, the screens and the tests use one copy of the rules.
 */
import { z } from "zod";

export const CUSTOM_KEY = /^c_[a-z0-9]{8}$/;
export const MAX_RULES = 10;
export const MAX_STEPS = 8;

/* ---------- Who gets it ---------- */

export const RULE_KINDS = [
  "tag",
  "service",
  "plan",
  "lastVisit",
  "nextVisit",
  "visits",
  "paid",
  "zip",
  "city",
  "since",
  "source",
  "kind",
  "prefers",
  "has",
] as const;
export type RuleKind = (typeof RULE_KINDS)[number];

/** What each rule asks for: its name in the picker, its choices, and whether it needs words (value) or a number (n). */
export const RULE_INFO: Record<
  RuleKind,
  { label: string; ops: { op: string; label: string }[]; value?: "pick" | "text"; n?: string }
> = {
  tag: {
    label: "Tag",
    ops: [
      { op: "has", label: "has the tag" },
      { op: "not", label: "does not have the tag" },
    ],
    value: "pick",
  },
  service: {
    label: "Work done",
    ops: [
      { op: "had", label: "had" },
      { op: "never", label: "never had" },
      { op: "notSince", label: "had, but not in the last" },
    ],
    value: "pick",
    n: "months",
  },
  plan: {
    label: "Plan",
    ops: [
      { op: "on", label: "is on a plan now" },
      { op: "not", label: "is not on a plan now" },
      { op: "never", label: "was never on a plan" },
      { op: "ended", label: "had a plan that ended" },
    ],
  },
  lastVisit: {
    label: "Last visit",
    ops: [
      { op: "within", label: "was in the last" },
      { op: "before", label: "was more than" },
      { op: "none", label: "never had one" },
    ],
    n: "days",
  },
  nextVisit: {
    label: "Next visit",
    ops: [
      { op: "yes", label: "is booked" },
      { op: "no", label: "is not booked" },
    ],
  },
  visits: {
    label: "Visits done",
    ops: [
      { op: "atLeast", label: "at least" },
      { op: "atMost", label: "at most" },
    ],
    n: "visits",
  },
  paid: {
    label: "Money paid, all time",
    ops: [
      { op: "atLeast", label: "at least $" },
      { op: "under", label: "under $" },
    ],
    n: "dollars",
  },
  zip: {
    label: "ZIP",
    ops: [
      { op: "in", label: "is one of" },
      { op: "notIn", label: "is not one of" },
    ],
    value: "text",
  },
  city: {
    label: "City",
    ops: [
      { op: "is", label: "is" },
      { op: "not", label: "is not" },
    ],
    value: "pick",
  },
  since: {
    label: "Client since",
    ops: [
      { op: "within", label: "the last" },
      { op: "before", label: "more than" },
    ],
    n: "days",
  },
  source: {
    label: "Found us by",
    ops: [
      { op: "is", label: "is" },
      { op: "not", label: "is not" },
    ],
    value: "pick",
  },
  kind: {
    label: "Home or business",
    ops: [
      { op: "home", label: "is a home" },
      { op: "business", label: "is a business" },
    ],
  },
  prefers: {
    label: "Likes to hear by",
    ops: [
      { op: "text", label: "text" },
      { op: "email", label: "email" },
    ],
  },
  has: {
    label: "Has",
    ops: [
      { op: "mobile", label: "a mobile number" },
      { op: "email", label: "an email" },
    ],
  },
};

export const audienceRule = z
  .object({
    kind: z.enum(RULE_KINDS),
    op: z.string().max(12),
    value: z.string().max(200),
    n: z.number().int().min(0).max(1_000_000),
  })
  .strict();
export type AudienceRule = z.infer<typeof audienceRule>;

export const audience = z.object({ match: z.enum(["all", "any"]), rules: z.array(audienceRule).max(MAX_RULES) }).strict();
export type Audience = z.infer<typeof audience>;

/** What the rules read about one client. Built on the server from its records; made up in the click-through preview. */
export interface Facts {
  tags: readonly string[];
  /** Kind of work done to the last day it was done ("" when the day is not known). */
  services: ReadonlyMap<string, string>;
  planNow: boolean;
  /** Ever had a repeating job, on now or not. */
  planEver: boolean;
  lastVisit: string;
  nextVisit: boolean;
  visits: number;
  paidCents: number;
  zips: readonly string[];
  cities: readonly string[];
  /** The day they became a client. */
  since: string;
  source: string;
  business: boolean;
  prefers: string;
  mobile: boolean;
  email: boolean;
}

const daysBefore = (today: string, n: number) =>
  new Date(Date.parse(`${today}T12:00:00Z`) - n * 86_400_000).toISOString().slice(0, 10);
const monthsBefore = (today: string, n: number) => {
  const [y, m, d] = today.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1 - n, d)).toISOString().slice(0, 10);
};
const same = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();
const zipList = (v: string) =>
  v
    .split(/[\s,]+/)
    .map((z) => z.trim().slice(0, 5))
    .filter((z) => /^\d{5}$/.test(z));

/** Does one rule fit this client? A rule missing what it needs fits nobody, so a half-made rule never widens the list. */
export function ruleFits(r: AudienceRule, f: Facts, today: string): boolean {
  switch (r.kind) {
    case "tag": {
      if (r.value.trim() === "") return false;
      const has = f.tags.some((t) => same(t, r.value));
      return r.op === "not" ? !has : has;
    }
    case "service": {
      if (r.value.trim() === "") return false;
      let last: string | undefined;
      for (const [name, day] of f.services) if (same(name, r.value)) last = day;
      if (r.op === "never") return last === undefined;
      if (last === undefined) return false;
      if (r.op === "notSince") return r.n > 0 && last !== "" && last <= monthsBefore(today, r.n);
      return true;
    }
    case "plan":
      return r.op === "on" ? f.planNow : r.op === "not" ? !f.planNow : r.op === "never" ? !f.planEver : f.planEver && !f.planNow;
    case "lastVisit":
      if (r.op === "none") return f.lastVisit === "";
      if (f.lastVisit === "" || r.n <= 0) return false;
      return r.op === "within" ? f.lastVisit > daysBefore(today, r.n) : f.lastVisit <= daysBefore(today, r.n);
    case "nextVisit":
      return r.op === "yes" ? f.nextVisit : !f.nextVisit;
    case "visits":
      return r.op === "atMost" ? f.visits <= r.n : f.visits >= r.n;
    case "paid":
      return r.op === "under" ? f.paidCents < r.n * 100 : f.paidCents >= r.n * 100;
    case "zip": {
      const want = zipList(r.value);
      if (want.length === 0) return false;
      const hit = f.zips.some((z) => want.includes(z.slice(0, 5)));
      return r.op === "notIn" ? !hit : hit;
    }
    case "city": {
      if (r.value.trim() === "") return false;
      const hit = f.cities.some((c) => same(c, r.value));
      return r.op === "not" ? !hit : hit;
    }
    case "since":
      if (f.since === "" || r.n <= 0) return false;
      return r.op === "within" ? f.since > daysBefore(today, r.n) : f.since <= daysBefore(today, r.n);
    case "source": {
      if (r.value.trim() === "") return false;
      const hit = same(f.source, r.value);
      return r.op === "not" ? !hit : hit;
    }
    case "kind":
      return r.op === "business" ? f.business : !f.business;
    case "prefers":
      return r.op === "email" ? f.prefers === "email" : f.prefers !== "email";
    case "has":
      return r.op === "email" ? f.email : f.mobile;
  }
}

/** Does the client fit the audience? No rules means everyone who may get marketing. */
export function audienceFits(a: Audience, f: Facts, today: string): boolean {
  if (a.rules.length === 0) return true;
  return a.match === "any" ? a.rules.some((r) => ruleFits(r, f, today)) : a.rules.every((r) => ruleFits(r, f, today));
}

/** One rule in plain words, for the campaign's page: "Work done had Window cleaning, not in the last 6 months". */
export function ruleWords(r: AudienceRule): string {
  const info = RULE_INFO[r.kind];
  if (r.kind === "service" && r.op === "notSince")
    return `${info.label} had ${r.value.trim()}, but not in the last ${String(r.n)} months`;
  const op = info.ops.find((o) => o.op === r.op)?.label ?? "";
  const parts = [info.label, op];
  if (info.value && r.value.trim()) parts.push(r.value.trim());
  const wantsN = info.n && !(r.kind === "service" && r.op !== "notSince") && !(r.kind === "lastVisit" && r.op === "none");
  if (wantsN) parts.push(r.kind === "paid" ? String(r.n) : `${String(r.n)} ${info.n!}`);
  if (r.kind === "lastVisit" && r.op === "before") parts.push("ago");
  if (r.kind === "since" && r.op === "before") parts.push("ago");
  return parts
    .join(" ")
    .replace(/\$ (\d)/, "$$$1")
    .trim();
}

/* ---------- What they get, and when ---------- */

export const CUSTOM_CHANNELS = ["sms", "email", "both"] as const;

export const customStep = z
  .object({
    id: z.string().max(12),
    /** Days after the person starts the campaign (day 0 is the day they start). */
    day: z.number().int().min(0).max(365),
    /** The local hour it may start going out, 9 AM to 5 PM. */
    hour: z.number().int().min(9).max(17),
    channel: z.enum(CUSTOM_CHANNELS),
    /** Both: send both, or only the way they like to hear from us (the other when that one is missing). */
    both: z.enum(["both", "prefers"]),
    /** No mobile number for a text step, or no email for an email step: send the other way instead. */
    fallback: z.boolean(),
    text: z.string().max(480),
    subject: z.string().max(120),
    body: z.string().max(3000),
    button: z.string().max(40),
    link: z.string().max(300),
  })
  .strict();
export type CustomStep = z.infer<typeof customStep>;

export const customStart = z.enum(["once", "joins"]);

/** What the team saves. The server keeps the rest (when it was turned on, who was in it then, today's counts). */
export const customBody = z
  .object({
    name: z.string().max(60),
    on: z.boolean(),
    /** once: everyone in the audience on the date starts that day. joins: each person the day they come into it. */
    start: customStart,
    date: z.string().regex(/^(\d{4}-\d\d-\d\d)?$/),
    audience,
    steps: z.array(customStep).min(1).max(MAX_STEPS),
  })
  .strict();
export type CustomBody = z.infer<typeof customBody>;

export interface CustomConfig extends CustomBody {
  /** When it was last turned on. */
  since: number;
  /** once: everyone was started on the date. */
  enrolled: boolean;
  /** joins: who was already in the audience when it was turned on, so only new people start. */
  baseline: string[];
  /** Today's sends, to keep each campaign to a few an hour. */
  today: { day: string; sms: number; email: number };
}

/** A text step needs its text, an email step a subject and words. The other way for a fallback is optional. */
export const needsText = (s: CustomStep) => s.channel !== "email";
export const needsEmail = (s: CustomStep) => s.channel !== "sms";

/** Which way a step goes to one person: by text, by email, both, or neither. */
export function stepRoute(
  s: CustomStep,
  who: { mobile: boolean; email: boolean; prefers: string },
): { sms: boolean; email: boolean } {
  const sms = who.mobile && s.text.trim() !== "";
  const mail = who.email && s.subject.trim() !== "" && s.body.trim() !== "";
  if (s.channel === "sms") return sms ? { sms: true, email: false } : { sms: false, email: s.fallback && mail };
  if (s.channel === "email") return mail ? { sms: false, email: true } : { sms: s.fallback && sms, email: false };
  if (s.both === "both") return { sms, email: mail };
  const wantsMail = who.prefers === "email";
  if (wantsMail && mail) return { sms: false, email: true };
  if (!wantsMail && sms) return { sms: true, email: false };
  return { sms, email: mail };
}

/** Fill-ins: {first} {name} {business} {service} (their last kind of work). */
export function fillCustom(words: string, who: { first: string; name: string; business: string; service: string }): string {
  return words
    .replace(/\{first\}/g, who.first)
    .replace(/\{name\}/g, who.name)
    .replace(/\{business\}/g, who.business)
    .replace(/\{service\}/g, who.service || "your last visit");
}

/** How many texts a message takes: 160 letters in one, 153 each when it runs longer (any emoji: 70 and 67). */
export function smsParts(words: string): number {
  if (words.length === 0) return 0;
  // Plain letters only: anything past Latin-1 (an emoji, curly quotes) makes the phone send it the long way.
  const plain = Array.from({ length: words.length }, (_, i) => words.charCodeAt(i)).every((c) => c <= 0xff);
  const one = plain ? 160 : 70;
  const more = plain ? 153 : 67;
  return words.length <= one ? 1 : Math.ceil(words.length / more);
}

/** What still keeps it from being turned on, in plain words. Empty: ready. */
export function customProblems(c: CustomBody): string[] {
  const out: string[] = [];
  if (c.name.trim() === "") out.push("Give it a name.");
  if (c.start === "once" && c.date === "") out.push("Pick the day it starts.");
  let prev = -1;
  for (const [i, s] of c.steps.entries()) {
    const n = `Step ${String(i + 1)}`;
    if (s.day <= prev) out.push(`${n} must come on a later day than the step before it.`);
    prev = s.day;
    if (needsText(s) && s.text.trim() === "") out.push(`${n} needs its text.`);
    if (needsEmail(s) && (s.subject.trim() === "" || s.body.trim() === "")) out.push(`${n} needs an email subject and words.`);
    if (s.button.trim() !== "" && !/^https:\/\/\S+$/.test(s.link.trim()))
      out.push(`${n} needs a link starting https:// for its button.`);
  }
  for (const r of c.audience.rules) {
    const info = RULE_INFO[r.kind];
    if (!info.ops.some((o) => o.op === r.op)) out.push(`Pick a choice for the ${info.label} rule.`);
    else if (info.value && r.value.trim() === "") out.push(`Fill in the ${info.label} rule.`);
  }
  return out;
}

export const newStepId = () => Math.random().toString(36).slice(2, 10);

export const blankStep = (day: number, channel: CustomStep["channel"] = "sms"): CustomStep => ({
  id: newStepId(),
  day,
  hour: 10,
  channel,
  both: "prefers",
  fallback: false,
  text: "",
  subject: "",
  body: "",
  button: "",
  link: "",
});

export function customDefaults(): CustomConfig {
  return {
    name: "",
    on: false,
    start: "once",
    date: "",
    audience: { match: "all", rules: [] },
    steps: [blankStep(0)],
    since: 0,
    enrolled: false,
    baseline: [],
    today: { day: "", sms: 0, email: 0 },
  };
}

/** A saved row read back safely: anything broken goes back to the defaults. */
export function customMerge(saved: unknown): CustomConfig {
  const d = customDefaults();
  const s = saved && typeof saved === "object" ? (saved as Record<string, unknown>) : {};
  const body = customBody.safeParse({
    name: s.name ?? d.name,
    on: s.on ?? d.on,
    start: s.start ?? d.start,
    date: s.date ?? d.date,
    audience: s.audience ?? d.audience,
    steps: s.steps ?? d.steps,
  });
  const t = s.today && typeof s.today === "object" ? (s.today as Record<string, unknown>) : {};
  return {
    ...(body.success ? body.data : { ...d, name: typeof s.name === "string" ? s.name.slice(0, 60) : "" }),
    since: typeof s.since === "number" ? s.since : 0,
    enrolled: s.enrolled === true,
    baseline: Array.isArray(s.baseline) ? s.baseline.filter((x): x is string => typeof x === "string") : [],
    today: {
      day: typeof t.day === "string" ? t.day : "",
      sms: typeof t.sms === "number" ? t.sms : 0,
      email: typeof t.email === "number" ? t.email : 0,
    },
  };
}

/** Starting points on New campaign. */
export const CUSTOM_TEMPLATES = ["blank", "seasonal", "upsell", "winback"] as const;
export type CustomTemplate = (typeof CUSTOM_TEMPLATES)[number];
export const TEMPLATE_INFO: Record<CustomTemplate, { name: string; what: string }> = {
  blank: { name: "Start from nothing", what: "Your own rules and steps." },
  seasonal: { name: "Seasonal offer", what: "A text, then an email 3 days later, to clients you pick." },
  upsell: { name: "Work they never had", what: "Offer a service to clients who never had it." },
  winback: { name: "Win back", what: "Clients with no visit in 6 months and nothing booked." },
};

export function customTemplate(t: CustomTemplate): CustomBody {
  const d = customDefaults();
  const base: CustomBody = { name: "", on: false, start: "once", date: "", audience: d.audience, steps: d.steps };
  if (t === "seasonal")
    return {
      ...base,
      name: "Seasonal offer",
      steps: [
        { ...blankStep(0), text: "Hi {first}, it's {business}. Book this month and get $20 off. Want a time?" },
        {
          ...blankStep(3, "email"),
          subject: "$20 off this month",
          body: "Hi {first},\n\nBook a visit this month and get $20 off. Just answer this email or tap below to pick a time.",
          button: "Book a visit",
        },
      ],
    };
  if (t === "upsell")
    return {
      ...base,
      name: "Work they never had",
      start: "joins",
      audience: { match: "all", rules: [{ kind: "service", op: "never", value: "", n: 0 }] },
      steps: [{ ...blankStep(0), text: "Hi {first}, it's {business}. We can add this to your next visit. Want us to?" }],
    };
  if (t === "winback")
    return {
      ...base,
      name: "Win back",
      start: "joins",
      audience: {
        match: "all",
        rules: [
          { kind: "lastVisit", op: "before", value: "", n: 180 },
          { kind: "nextVisit", op: "no", value: "", n: 0 },
        ],
      },
      steps: [
        { ...blankStep(0), text: "Hi {first}, it's {business}. It's been a while. Want us to come by again?" },
        {
          ...blankStep(7, "email"),
          subject: "We miss you, {first}",
          body: "Hi {first},\n\nIt's been a while since your last {service}. Answer this email and we'll find you a time.",
        },
      ],
    };
  return base;
}

/* ---------- API shapes ---------- */

export const customRow = z.object({
  key: z.string(),
  name: z.string(),
  what: z.string(),
  on: z.boolean(),
  line: z.string(),
});
export type CustomRow = z.infer<typeof customRow>;

export const customCount = z.object({
  people: z.number(),
  sms: z.number(),
  email: z.number(),
  /** Up to 100 names, most recent clients first. */
  names: z.array(z.object({ name: z.string(), sms: z.boolean(), email: z.boolean() })),
});
export type CustomCount = z.infer<typeof customCount>;

export const customPage = z.object({
  key: z.string(),
  name: z.string(),
  on: z.boolean(),
  start: customStart,
  date: z.string(),
  since: z.number(),
  audience,
  steps: z.array(customStep),
  count: customCount,
  /** In it now, by where they are: still going, done, stopped. */
  people: z.object({ going: z.number(), done: z.number(), stopped: z.number() }),
  stats: z.record(z.string(), z.number()),
  problems: z.array(z.string()),
  pickers: z.object({
    tags: z.array(z.string()),
    services: z.array(z.string()),
    sources: z.array(z.string()),
    cities: z.array(z.string()),
  }),
  business: z.string(),
  /** Added to the first text anyone gets from any campaign. */
  optout: z.string(),
});
export type CustomPage = z.infer<typeof customPage>;

export const customCreate = z.object({ template: z.enum(CUSTOM_TEMPLATES) }).strict();
export const customCountBody = z.object({ audience }).strict();
